import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { confirmEmailCode, sendEmailChallenge } from '@client/new/api/auth.api';

type Params = {
  isOpen: boolean;
  email: string;
  onSuccessfulCodeVerification: (token: string) => Promise<unknown> | unknown;
};

export const useCodeVerificationModal = ({
  isOpen,
  email,
  onSuccessfulCodeVerification,
}: Params) => {
  const [code, setCode] = React.useState('');
  const [codeError, setCodeError] = React.useState<string>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) {
      setCode('');
      setCodeError(undefined);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const verifyCode = async (submittedCode: string) => {
    setIsSubmitting(true);
    setCodeError(undefined);

    try {
      const { token } = await confirmEmailCode(
        email,
        submittedCode,
        'update-signin-email',
      );
      if (!token) {
        setIsSubmitting(false);
        setCode('');
        toast.error('Failed to verify code');
        return;
      }
      await onSuccessfulCodeVerification(token);
    } catch (error) {
      setIsSubmitting(false);
      setCode('');
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      if (status === 401) {
        setCodeError('Invalid or expired code');
      } else {
        toast.error('Failed to verify code');
      }
    }
  };

  const onResend = async () => {
    try {
      await sendEmailChallenge(email, 'update-signin-email');
      toast.success('Code sent again');
    } catch {
      toast.error('Failed to resend code');
    }
  };

  return {
    code,
    setCode,
    codeError,
    isSubmitting,
    verifyCode,
    onResend,
  };
};
